/**
 * Users API Service (PR A7)
 * API calls for user management
 */
import { apiRequest } from './adminApi';

/**
 * List users with pagination, search, role/status filters, and sorting.
 * Uses /admin/users endpoint which returns roles.
 *
 * Feb 28, 2026 — Extended for the Admin Users Management redesign:
 * opts into the backend's `paginated=true` wrapper so the UI can show
 * total counts, page numbers, and First/Last navigation reliably.
 */
export const listUsers = async ({
  page = 1,
  pageSize = 25,
  query = '',
  roleFilter = '',
  statusFilter = '',
  sortBy = '',
  sortOrder = 'desc',
} = {}) => {
  const params = new URLSearchParams({
    limit: pageSize.toString(),
    offset: ((page - 1) * pageSize).toString(),
    paginated: 'true',
  });
  if (query && query.trim()) params.append('search', query.trim());
  if (roleFilter && roleFilter.trim()) params.append('role_filter', roleFilter.trim());
  if (statusFilter && statusFilter.trim()) params.append('status_filter', statusFilter.trim());
  if (sortBy && sortBy.trim()) {
    params.append('sort_by', sortBy.trim());
    params.append('sort_order', (sortOrder || 'desc').toLowerCase() === 'asc' ? 'asc' : 'desc');
  }
  return await apiRequest(`/admin/users?${params.toString()}`);
};

/**
 * Get single user by ID
 */
export const getUser = async (userId) => {
  return await apiRequest(`/admin/users/${userId}`);
};

/**
 * Reset user password
 */
export const resetUserPassword = async (userId, reason = '') => {
  return await apiRequest(`/admin/users/${userId}/password-reset/issue`, {
    method: 'POST',
    body: JSON.stringify({ reason })
  });
};

/**
 * Admin-initiated IMMEDIATE password reset (P1).
 *
 * Generates a temporary password server-side, returns it ONCE in the
 * response, clears lockout state, and forces the target user to change
 * their password on next login. Admin only.
 *
 * @param {string} userId - Target user UUID
 * @param {string} reason - Human-readable reason, written to audit log
 * @returns {Promise<{user_id, user_email, temporary_password, password_reset_required, message}>}
 */
export const adminResetUserPassword = async (userId, reason = '') => {
  return await apiRequest(`/admin/users/${userId}/admin-reset-password`, {
    method: 'POST',
    body: JSON.stringify({ reason, confirm: true })
  });
};

/**
 * Change the currently-authenticated user's password (P1).
 * Used by the forced-change modal after an admin temp-password reset.
 *
 * @param {string} currentPassword
 * @param {string} newPassword
 */
export const changeMyPassword = async (currentPassword, newPassword) => {
  return await apiRequest(`/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword
    })
  });
};

/**
 * Lock user account
 */
export const lockUser = async (userId, reason = '') => {
  return await apiRequest(`/admin/users/${userId}/lock`, {
    method: 'PUT',
    body: JSON.stringify({ reason })
  });
};

/**
 * Unlock user account
 */
export const unlockUser = async (userId) => {
  return await apiRequest(`/admin/users/${userId}/unlock`, {
    method: 'PUT'
  });
};

/**
 * Resend verification email to a user
 */
export const resendVerificationEmail = async (userId) => {
  return await apiRequest(`/admin/users/${userId}/resend-verification`, {
    method: 'POST'
  });
};

/**
 * Start impersonating user
 */
export const startImpersonation = async (userId, reason = '') => {
  return await apiRequest(`/admin/impersonate/start`, {
    method: 'POST',
    body: JSON.stringify({
      target_user_id: userId,
      reason
    })
  });
};

/**
 * Get user's roles
 */
export const getUserRoles = async (userId) => {
  return await apiRequest(`/admin/users/${userId}/roles`);
};

/**
 * Get user's audit logs


/**
 * Create new user (PR A17)
 */
export const createUser = async (userData) => {
  return await apiRequest(`/admin/users`, {
    method: 'POST',
    body: JSON.stringify(userData)
  });
};

/**
 * Update user (PR A17)
 */
export const updateUser = async (userId, userData) => {
  return await apiRequest(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData)
  });
};

/**
 * Deactivate user (PR A17)
 */
export const deactivateUser = async (userId, reason = '') => {
  return await apiRequest(`/admin/users/${userId}/deactivate`, {
    method: 'PUT',
    body: JSON.stringify({ reason })
  });
};

/**
 * Reactivate user (PR A17)
 */
export const reactivateUser = async (userId) => {
  return await apiRequest(`/admin/users/${userId}/reactivate`, {
    method: 'PUT'
  });
};

/**
 * Assign roles to user (PR A17)
 */
export const assignRoles = async (userId, roleIds) => {
  return await apiRequest(`/admin/users/${userId}/roles`, {
    method: 'PUT',
    body: JSON.stringify({ role_ids: roleIds })
  });
};

/**
 * List all available roles (PR A17)
 */
export const listRoles = async () => {
  // Use /roles endpoint instead of /admin/roles
  // /roles requires only admin role, while /admin/roles requires rbac.view permission
  return await apiRequest(`/roles`);
};

/** ROLE-COMPAT-001: the Primary + Additional role matrix enforced by the API */
export const getRoleCompatibility = async () => {
  return await apiRequest(`/admin/users/role-compatibility`);
};

/**
 * Get user's audit logs
 */
export const getUserAuditLogs = async (userId, { page = 1, pageSize = 25 }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString()
  });
  
  return await apiRequest(`/admin/users/${userId}/audit-logs?${params.toString()}`);
};

/**
 * Stop impersonating current user
 */
export const stopImpersonation = async () => {
  return await apiRequest(`/admin/impersonate/stop`, {
    method: 'POST'
  });
};

/**
 * Get user's orders (PR A13)
 */
export const getUserOrders = async (userId, { page = 1, pageSize = 10, statusFilter = null }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString()
  });
  
  if (statusFilter) {
    params.append('status_filter', statusFilter);
  }
  
  return await apiRequest(`/admin/users/${userId}/orders?${params.toString()}`);
};

/**
 * Get user's order statistics (PR A13)
 */
export const getUserOrderStats = async (userId) => {
  return await apiRequest(`/admin/users/${userId}/order-stats`);
};

/**
 * P0 QC Workflow: Update user's requires_qc flag (Slice 5)
 * Admin-only endpoint to toggle QC requirement for a user
 */
export const updateUserQCRequirement = async (userId, requiresQC) => {
  return await apiRequest(`/admin/users/${userId}/requires-qc?requires_qc=${requiresQC}`, {
    method: 'PATCH'
  });
};

/** FIN-VIS-001: Admin-only toggle for per-user "View Search Pricing & Margin" (read-only financial visibility). */
export const updateUserFinanceVisibility = async (userId, enabled) => {
  return await apiRequest(`/admin/users/${userId}/view-search-pricing-margin?enabled=${enabled}`, {
    method: 'PATCH'
  });
};

/**
 * P0 QC Workflow: Get user activity events from activity_events collection
 * Used for User Profile drawer Activity Logs tab
 */
export const getUserActivityEvents = async (userId, { page = 1, page_size = 10 } = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: page_size.toString()
  });
  return await apiRequest(`/admin/users/${userId}/activity-events?${params.toString()}`);
};

/**
 * P2 QC Product Scope: Update user's qc_product_scope
 * Admin-only endpoint to set which products a QC Specialist can review
 * @param {string} userId - User ID
 * @param {string[]} scope - Array of product types, e.g., ["tax"], ["title"], ["tax", "title"]
 */
export const updateUserQCProductScope = async (userId, scope) => {
  return await apiRequest(`/admin/users/${userId}/qc-product-scope`, {
    method: 'PATCH',
    body: JSON.stringify({ qc_product_scope: scope })
  });
};
